import { useEffect, useRef, type HTMLAttributes, type KeyboardEvent, type ReactNode } from 'react';
import { clsx } from '../clsx';
import styles from './MenuList.module.css';

export interface MenuListProps extends HTMLAttributes<HTMLDivElement> {
  /** Accessible name of the menu ("Tools", "Profil"). */
  label?: string | undefined;
  labelledBy?: string | undefined;
  /** `default`: 10/12 items, 13 px (topbar menus); `compact`: 9/14 items, 12 px (card menus). */
  density?: 'default' | 'compact' | undefined;
  /** Focus the first item when the menu mounts (default true). */
  autoFocus?: boolean | undefined;
  /** Called on Escape or Tab so the owner can close the menu. */
  onClose?: (() => void) | undefined;
  children: ReactNode;
}

const ITEM_SELECTOR = '[role="menuitem"]:not([aria-disabled="true"])';

/** `role="menu"` container: arrow keys, Home/End and first-letter typeahead move between the items. */
export function MenuList({
  label,
  labelledBy,
  density = 'default',
  autoFocus = true,
  onClose,
  className,
  children,
  onKeyDown,
  ...rest
}: MenuListProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!autoFocus) return;
    const first = ref.current?.querySelector<HTMLElement>(ITEM_SELECTOR);
    first?.focus();
  }, [autoFocus]);

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    onKeyDown?.(event);
    if (event.defaultPrevented) return;
    const items = Array.from(ref.current?.querySelectorAll<HTMLElement>(ITEM_SELECTOR) ?? []);
    if (items.length === 0) return;
    const index = items.findIndex((item) => item === document.activeElement);
    let next = -1;
    switch (event.key) {
      case 'ArrowDown':
        next = (index + 1) % items.length;
        break;
      case 'ArrowUp':
        next = (index - 1 + items.length) % items.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = items.length - 1;
        break;
      case 'Escape':
      case 'Tab':
        onClose?.();
        return;
      default: {
        if (event.key.length === 1 && /\S/.test(event.key)) {
          const letter = event.key.toLocaleLowerCase('de');
          const order = [...items.slice(index + 1), ...items.slice(0, index + 1)];
          const hit = order.find((item) =>
            (item.textContent ?? '').trim().toLocaleLowerCase('de').startsWith(letter),
          );
          if (hit) next = items.indexOf(hit);
        }
      }
    }
    if (next >= 0) {
      event.preventDefault();
      items[next]?.focus();
    }
  }

  return (
    <div
      ref={ref}
      role="menu"
      aria-label={label}
      aria-labelledby={labelledBy}
      data-density={density}
      className={clsx(styles.menu, density === 'compact' && styles.compact, className)}
      onKeyDown={handleKeyDown}
      {...rest}
    >
      {children}
    </div>
  );
}

/** Hairline between item groups. */
export function MenuSeparator() {
  return <div role="separator" className={styles.separator} />;
}

export interface MenuHeaderProps {
  /** First line, 13.5 px medium (user name). */
  title: ReactNode;
  /** Second line, 11.5 px subtle (role label). */
  subtitle?: ReactNode | undefined;
}

/** Head of the profile menu: name and role label above the items. */
export function MenuHeader({ title, subtitle }: MenuHeaderProps) {
  return (
    <div className={styles.header}>
      <div className={styles.headerTitle}>{title}</div>
      {subtitle ? <div className={styles.headerSubtitle}>{subtitle}</div> : null}
    </div>
  );
}
